import { Button } from '@atlas/components';
import cx from 'classnames';
import { useCallback } from 'react';
import { Route, useRoute } from 'wouter';

import DepositButton from 'components/DepositButton';
import Icon from 'components/Icon/Icon';
import Link from 'components/Link';
import Logo from 'components/Logo';
import Switcher from 'components/Switcher';
import { useConfig } from 'config';
import {
  AuthStatus,
  SidebarSize,
  useClientProfile,
  useUISettings,
} from 'store';
import useLayout, { LayoutType } from 'utils/layout';
import { notAuthPathRegexp } from 'utils/location';
import { Translation } from 'utils/translations';
import useIntl from 'utils/useIntl';

import styles from './Header.module.scss';

const Header = () => {
  const isSearchEnabled = useConfig('config.siteConfig.header.search');
  const [clientProfile] = useClientProfile();
  const [uiSettings, { setLeftSidebar }] = useUISettings();
  const [isSignInPage] = useRoute('/signin');
  const [isResetPasswordPage] = useRoute('/reset-password');
  const [isSignUpPage] = useRoute('/signup');
  const [isCasinoPage] = useRoute(/\/(live-|)casino/);
  const layout = useLayout();
  const { formatMoney } = useIntl();

  const toggleSidebar = useCallback(() => {
    const size =
      uiSettings.leftSidebar === SidebarSize.collapsed
        ? SidebarSize.expanded
        : SidebarSize.collapsed;

    setLeftSidebar(size);
  }, [setLeftSidebar, uiSettings.leftSidebar]);

  const isMobile = layout === LayoutType.mobile;
  const shouldRenderSearch = isSearchEnabled && isCasinoPage && !isMobile;
  const shouldRenderLoginButton = !isSignInPage && !isResetPasswordPage;

  const sidebarExpanded = uiSettings.leftSidebar === SidebarSize.expanded;

  const balance =
    typeof clientProfile.data?.wallet.balance === 'number'
      ? `${formatMoney(clientProfile.data.wallet.balance)}`
      : null;

  const bonus =
    typeof clientProfile.data?.wallet.bonuses[0]?.balance === 'number'
      ? `${formatMoney(clientProfile.data.wallet.bonuses[0].balance)}`
      : null;

  const authLinkState = {
    from:
      history.state?.from ||
      `${window.location.pathname}${window.location.search}`,
  };

  return (
    <div
      className={cx(styles.wrapper, {
        [styles.mobile]: isMobile,
      })}
    >
      {!isMobile && (
        <Route path={notAuthPathRegexp}>
          <aside
            className={cx(styles['left-buttons'], {
              [styles.expanded]: sidebarExpanded,
            })}
          >
            <button
              type="button"
              onClick={() => toggleSidebar()}
              className={styles['menu-button']}
            >
              <Icon
                name={
                  uiSettings.leftSidebar === SidebarSize.collapsed
                    ? 'menu-expand'
                    : 'menu-collapse'
                }
              />
            </button>
            {sidebarExpanded && <Switcher wide />}
          </aside>
        </Route>
      )}

      <div className={styles['content-wrapper']}>
        <div className={styles.content}>
          <Logo />
          <nav className={styles['right-buttons']}>
            {shouldRenderSearch && (
              <Link to="/casino/search" className={styles.search}>
                <Icon name="search-normal.outline" />
              </Link>
            )}

            {clientProfile.status === AuthStatus.signedIn && (
              <>
                {!isMobile && (
                  <Link to="/account" className={styles.profile}>
                    <Icon name="profile-circle.outline" />
                  </Link>
                )}
                <div
                  className={cx(styles['balance-wrapper'], {
                    [styles['with-bonus']]: bonus,
                    [styles['balance-hidden']]: uiSettings.hideBalance,
                  })}
                >
                  {!uiSettings.hideBalance && balance && (
                    <div className={styles.balance}>{balance}</div>
                  )}
                  {!uiSettings.hideBalance && bonus && (
                    <div className={styles.bonus}>
                      <Icon name="gift3.bold" width={16} height={16} />
                      {bonus}
                    </div>
                  )}
                  <DepositButton className={styles.deposit} />
                </div>
              </>
            )}
            {clientProfile.status === AuthStatus.signedOut && (
              <>
                {shouldRenderLoginButton && (
                  <Link to="/signin" state={authLinkState}>
                    {isSignUpPage ? (
                      <Button>
                        <Translation message="log_in_button" />
                      </Button>
                    ) : (
                      <Translation message="log_in_button" />
                    )}
                  </Link>
                )}
                {!isSignUpPage && (
                  <Link to="/signup" state={authLinkState}>
                    <Button>
                      <Translation message="register_button" />
                    </Button>
                  </Link>
                )}
              </>
            )}
          </nav>
        </div>
      </div>
    </div>
  );
};

export default Header;
